/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Frequently Asked Questions section for AI Image Enhancer
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is an AI image enhancer?',
      answer:
        'An AI image enhancer is a tool that uses artificial intelligence to automatically improve the quality of photos and images. It can increase resolution, reduce noise, sharpen details, correct colors, and fix common issues like blur or pixelation—all without manual editing.',
    },
    {
      id: 'faq-2',
      question: 'How does FalcoCut AI Image Enhancer work?',
      answer:
        'FalcoCut uses advanced deep learning models trained on millions of images to analyze and enhance your photos. When you upload an image, the AI identifies areas that need improvement and applies intelligent corrections to boost clarity, detail, and overall quality while maintaining a natural look.',
    },
    {
      id: 'faq-3',
      question: 'What image formats are supported?',
      answer:
        'FalcoCut AI Image Enhancer supports all common image formats including JPG, JPEG, PNG, WebP, and BMP. You can upload images in any of these formats and download the enhanced results in your preferred format.',
    },
    {
      id: 'faq-4',
      question: 'Is there a file size limit?',
      answer:
        'Yes, the maximum file size for uploads is 10MB per image. For optimal processing speed and results, we recommend images between 1MB and 5MB. Larger files may take longer to process but will still receive full enhancement treatment.',
    },
    {
      id: 'faq-5',
      question: 'How long does the enhancement process take?',
      answer:
        'Most images are enhanced within 5-15 seconds, depending on the image size and complexity. Our optimized AI engine processes images quickly while maintaining high-quality results. Batch processing of multiple images is also available for efficiency.',
    },
    {
      id: 'faq-6',
      question: 'Will enhancing my image reduce its original quality?',
      answer:
        'No, FalcoCut AI Image Enhancer is designed to improve quality, not reduce it. The AI adds detail and clarity rather than compressing or degrading your image. The enhanced output is always equal to or better than the original in terms of visual quality.',
    },
    {
      id: 'faq-7',
      question: 'Can I enhance old or damaged photos?',
      answer:
        'Yes, FalcoCut is excellent for restoring old, faded, or damaged photos. The AI can reduce noise, improve clarity, enhance colors, and repair common issues like scratches or blur. While it cannot recreate missing portions of images, it significantly improves the visible content.',
    },
    {
      id: 'faq-8',
      question: 'Is my data secure?',
      answer:
        'Absolutely. FalcoCut takes data security seriously. Your uploaded images are processed using encrypted connections and are automatically deleted from our servers after processing. We do not store, share, or use your images for any purpose other than the enhancement you requested.',
    },
    {
      id: 'faq-9',
      question: 'Can I use the enhanced images commercially?',
      answer:
        'Yes, you retain full rights to your images. Once enhanced, you can use them for any purpose including commercial projects, social media, print materials, and more. FalcoCut does not claim any ownership or rights over your original or enhanced images.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
